import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '../ui/Badge';
import { AlignmentTypeahead } from './AlignmentTypeahead';
import { AlignmentHierarchyTree, type HierarchicalVision } from './AlignmentHierarchyTree';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/Tabs';
import { EyeIcon, ExclamationTriangleIcon, UserIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../state/auth/AuthProvider';
import { useLanguage } from '../../state/language/LanguageProvider';
import api from '../../services/api';
import { useAlignmentRoadmapScope } from './AlignmentRoadmapScopeContext';

interface Vision {
  id: string;
  name: string;
  description: string;
  status: string;
}

interface Strategy {
  id: string;
  name: string;
  description: string;
  strategy_type: string;
  status: string;
}

interface KPI {
  id: string;
  name: string;
  category: string;
  current_value: number;
  target_value: number;
  unit: string;
}

interface Project {
  id: string;
  name: string;
  description: string;
  status: string;
}

interface OrphanItem {
  id: string;
  name: string;
  description: string;
  status: string;
  orphan_type: string;
  roadmap_id?: string;
  roadmap_name?: string;
}

interface HandicapItem {
  id: string;
  name: string;
  description: string;
  status: string;
  handicap_type: string;
}

interface OrphansReport {
  objectives: OrphanItem[];
  strategies: OrphanItem[];
  projects: OrphanItem[];
  /** Always empty — KPIs without strategy are not treated as orphans. */
  kpis?: OrphanItem[];
  summary: {
    total_orphans: number;
    orphan_objectives: number;
    orphan_strategies: number;
    orphan_projects: number;
    orphan_kpis?: number;
  };
}

interface HandicapsReport {
  visions: HandicapItem[];
  objectives: HandicapItem[];
  strategies: HandicapItem[];
  strategiesWithoutProjects: HandicapItem[];
  summary: {
    total_handicaps: number;
    vision_handicaps: number;
    objective_handicaps: number;
    strategy_kpi_handicaps: number;
    strategy_project_handicaps: number;
  };
}

type HierarchyFilters = {
  visionId: string;
  strategyId: string;
  projectId: string;
  kpiId: string;
};

/**
 * Narrow the linked forest client-side. The hierarchical API returns the
 * full scoped tree; vision / strategy / project / KPI picks only hide branches.
 */
function filterHierarchy(visions: HierarchicalVision[], filters: HierarchyFilters): HierarchicalVision[] {
  return visions
    .filter((vision) => !filters.visionId || vision.vision_id === filters.visionId)
    .map((vision) => ({
      ...vision,
      objectives: (vision.objectives || [])
        .filter((objective) => !filters.kpiId || objective.objective_kpi_id === filters.kpiId)
        .map((objective) => ({
          ...objective,
          strategies: (objective.strategies || [])
            .filter((strategy) => {
              if (filters.strategyId && strategy.strategy_id !== filters.strategyId) {
                return false;
              }
              if (
                filters.projectId &&
                !(strategy.projects || []).some((project) => project.project_id === filters.projectId)
              ) {
                return false;
              }
              return true;
            })
            .map((strategy) => ({
              ...strategy,
              projects: filters.projectId
                ? (strategy.projects || []).filter((project) => project.project_id === filters.projectId)
                : strategy.projects
            }))
        }))
        .filter((objective) => {
          if (filters.strategyId || filters.projectId) {
            return (objective.strategies || []).length > 0;
          }
          return true;
        })
    }))
    .filter((vision) => {
      if (filters.strategyId || filters.projectId || filters.kpiId) {
        return (vision.objectives || []).length > 0;
      }
      return true;
    });
}

/**
 * When a filter is on, hide Unlinked unless the orphan is the selected
 * strategy or project. Orphans have no vision / KPI path.
 */
function isFiltering(filters: HierarchyFilters): boolean {
  return Boolean(filters.visionId || filters.strategyId || filters.projectId || filters.kpiId);
}

function filterOrphans(orphans: OrphansReport | null, filters: HierarchyFilters): OrphansReport | null {
  if (!orphans) {
    return null;
  }
  if (!isFiltering(filters)) {
    return orphans;
  }
  const objectives: OrphanItem[] = [];
  const strategies = filters.strategyId
    ? (orphans.strategies || []).filter((row) => row.id === filters.strategyId)
    : [];
  const projects = filters.projectId
    ? (orphans.projects || []).filter((row) => row.id === filters.projectId)
    : [];
  return {
    ...orphans,
    objectives,
    strategies,
    projects,
    summary: {
      orphan_objectives: objectives.length,
      orphan_strategies: strategies.length,
      orphan_projects: projects.length,
      orphan_kpis: 0,
      total_orphans: objectives.length + strategies.length + projects.length
    }
  };
}

function filterHandicaps(report: HandicapsReport | null, filters: HierarchyFilters): HandicapsReport | null {
  if (!report) {
    return null;
  }
  if (!isFiltering(filters)) {
    return report;
  }
  const visions = filters.visionId
    ? (report.visions || []).filter((row) => row.id === filters.visionId)
    : [];
  const objectives: HandicapItem[] = [];
  const strategies = filters.strategyId
    ? (report.strategies || []).filter((row) => row.id === filters.strategyId)
    : [];
  const strategiesWithoutProjects = filters.strategyId
    ? (report.strategiesWithoutProjects || []).filter((row) => row.id === filters.strategyId)
    : [];
  return {
    ...report,
    visions,
    objectives,
    strategies,
    strategiesWithoutProjects,
    summary: {
      vision_handicaps: visions.length,
      objective_handicaps: objectives.length,
      strategy_kpi_handicaps: strategies.filter((row) => row.handicap_type === 'STRATEGY_WITHOUT_KPIS').length,
      strategy_project_handicaps: strategiesWithoutProjects.length,
      total_handicaps:
        visions.length +
        objectives.length +
        strategies.filter((row) => row.handicap_type === 'STRATEGY_WITHOUT_KPIS').length +
        strategiesWithoutProjects.length
    }
  };
}

export const AlignmentReports: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { scope, listParams, reportBody, scopeKey } = useAlignmentRoadmapScope();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('alignment');
  
  // Filter states
  const [selectedVision, setSelectedVision] = useState<string>('');
  const [selectedStrategy, setSelectedStrategy] = useState<string>('');
  const [selectedKPI, setSelectedKPI] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  
  // Data states
  const [visions, setVisions] = useState<Vision[]>([]);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [hierarchicalReport, setHierarchicalReport] = useState<HierarchicalVision[]>([]);
  const [treeOrphans, setTreeOrphans] = useState<OrphansReport | null>(null);
  const [orphansReport, setOrphansReport] = useState<OrphansReport | null>(null);
  const [handicapsReport, setHandicapsReport] = useState<HandicapsReport | null>(null);
  const [treeLoaded, setTreeLoaded] = useState(false);

  useEffect(() => {
    if (user?.tenantId) {
      fetchFilterData();
    }
  }, [user?.tenantId, scopeKey]);

  useEffect(() => {
    setHierarchicalReport([]);
    setTreeOrphans(null);
    setOrphansReport(null);
    setHandicapsReport(null);
    setTreeLoaded(false);
  }, [scopeKey]);

  const fetchFilterData = async () => {
    try {
      const [visionsResponse, strategiesResponse, kpisResponse, projectsResponse] = await Promise.all([
        api.get(`/alignment/visions/${user?.tenantId}`, listParams),
        api.get(`/alignment/strategies/${user?.tenantId}`, listParams),
        api.get('/kpis'),
        api.get('/transformations')
      ]);

      if (visionsResponse.data.success) setVisions(visionsResponse.data.data);
      if (strategiesResponse.data.success) setStrategies(strategiesResponse.data.data);
      if (kpisResponse.data.success) setKpis(kpisResponse.data.data);
      if (projectsResponse.data.success) {
        const allProjects = projectsResponse.data.data || [];
        setProjects(
          scope.mode === 'selected'
            ? allProjects.filter((project: { roadmap_id?: string }) =>
                project.roadmap_id && scope.roadmapIds.includes(project.roadmap_id)
              )
            : allProjects
        );
      }
    } catch (err) {
      setError(t('alignment.reports.errors.failedToFetchFilterData'));
      console.error('Error fetching filter data:', err);
    }
  };

  const generateAlignmentReport = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const filters = { tenant_id: user.tenantId, ...reportBody };
      const [hierarchyResponse, orphansResponse] = await Promise.all([
        api.post('/alignment/reports/hierarchical', filters),
        api.post('/alignment/reports/orphans', filters)
      ]);

      if (!hierarchyResponse.data.success) {
        throw new Error(hierarchyResponse.data.message || t('alignment.reports.errors.failedToGenerateReport'));
      }

      setHierarchicalReport(hierarchyResponse.data.data.hierarchical_report || []);
      setTreeOrphans(orphansResponse.data.success ? orphansResponse.data.data : null);
      setTreeLoaded(true);
    } catch (err) {
      console.error('Error in generateAlignmentReport:', err);
      setError(err instanceof Error ? err.message : t('alignment.reports.errors.failedToGenerateAlignmentReport'));
    } finally {
      setLoading(false);
    }
  }, [user, reportBody, t]);

  const generateOrphansReport = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/alignment/reports/orphans', { tenant_id: user.tenantId, ...reportBody });
      
      if (response.data.success) {
        setOrphansReport(response.data.data);
      } else {
        throw new Error(response.data.message || t('alignment.reports.errors.failedToGenerateOrphansReport'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.reports.errors.failedToGenerateOrphansReport'));
    } finally {
      setLoading(false);
    }
  };

  const generateHandicapsReport = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/alignment/reports/handicaps', { tenant_id: user.tenantId, ...reportBody });
      
      if (response.data.success) {
        setHandicapsReport(response.data.data);
      } else {
        throw new Error(response.data.message || t('alignment.reports.errors.failedToGenerateHandicapsReport'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.reports.errors.failedToGenerateHandicapsReport'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.tenantId) {
      return;
    }
    if (activeTab === 'alignment') {
      void generateAlignmentReport();
    } else if (activeTab === 'handicaps') {
      void generateHandicapsReport();
    } else if (activeTab === 'orphans') {
      void generateOrphansReport();
    }
    // Load the open tab when the user picks it or the roadmap scope changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user?.tenantId, scopeKey]);

  const clearFilters = () => {
    setSelectedVision('');
    setSelectedStrategy('');
    setSelectedKPI('');
    setSelectedProject('');
  };

  const hierarchyFilters = useMemo(
    () => ({
      visionId: selectedVision,
      strategyId: selectedStrategy,
      projectId: selectedProject,
      kpiId: selectedKPI
    }),
    [selectedVision, selectedStrategy, selectedProject, selectedKPI]
  );

  const filteredVisions = useMemo(
    () => filterHierarchy(hierarchicalReport, hierarchyFilters),
    [hierarchicalReport, hierarchyFilters]
  );

  const filteredOrphans = useMemo(
    () => filterOrphans(treeOrphans, hierarchyFilters),
    [treeOrphans, hierarchyFilters]
  );

  const filteredHandicaps = useMemo(
    () => filterHandicaps(handicapsReport, hierarchyFilters),
    [handicapsReport, hierarchyFilters]
  );

  const filteredOrphansTab = useMemo(
    () => filterOrphans(orphansReport, hierarchyFilters),
    [orphansReport, hierarchyFilters]
  );

  const filtersRow = (
    <div className="flex flex-wrap items-center gap-2">
      <AlignmentTypeahead
        className="w-44"
        value={selectedVision}
        onChange={setSelectedVision}
        options={visions.map(v => ({ id: v.id, label: v.name }))}
        placeholder={t('alignment.reports.allVisions')}
      />
      <AlignmentTypeahead
        className="w-44"
        value={selectedStrategy}
        onChange={setSelectedStrategy}
        options={strategies.map(s => ({ id: s.id, label: s.name }))}
        placeholder={t('alignment.reports.allStrategies')}
      />
      <AlignmentTypeahead
        className="w-44"
        value={selectedKPI}
        onChange={setSelectedKPI}
        options={kpis.map(k => ({ id: k.id, label: (k as any).custom_name || (k as any).master_name }))}
        placeholder={t('alignment.reports.allKPIs')}
      />
      <AlignmentTypeahead
        className="w-44"
        value={selectedProject}
        onChange={setSelectedProject}
        options={projects.map(p => ({ id: p.id, label: p.name }))}
        placeholder={t('alignment.reports.allProjects')}
      />
      <button
        type="button"
        onClick={clearFilters}
        disabled={!selectedVision && !selectedStrategy && !selectedKPI && !selectedProject}
        className="shrink-0 text-sm underline text-[var(--color-accent-bg)] hover:opacity-80 disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
      >
        {t('alignment.reports.clearFilters')}
      </button>
    </div>
  );

  return (
    <div className="space-y-3">

      {/* Error Display */}
      {error && (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary-bg)] p-4">
          <div className="flex items-center gap-2 text-[var(--color-error)]">
            <span className="text-sm">⚠️</span>
            <span className="text-sm font-medium">{t('alignment.common.error')}</span>
          </div>
          <p className="text-sm text-[var(--color-secondary-text)] mt-1">{error}</p>
        </div>
      )}

      {filtersRow}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="!border-b-0 mb-4">
          <TabsTrigger value="alignment" className="flex items-center gap-2">
            <EyeIcon className="w-4 h-4" />
            {t('alignment.reports.tree.title')}
          </TabsTrigger>
          <TabsTrigger value="handicaps" className="flex items-center gap-2">
            <UserIcon className="w-4 h-4" />
            {t('alignment.reports.tabs.handicaps')}
          </TabsTrigger>
          <TabsTrigger value="orphans" className="flex items-center gap-2">
            <ExclamationTriangleIcon className="w-4 h-4" />
            {t('alignment.reports.tabs.orphans')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="alignment" className="space-y-3">
          {loading && !treeLoaded ? (
            <p className="text-sm text-[var(--color-secondary-text)]">{t('alignment.reports.generating')}</p>
          ) : treeLoaded ? (
            <AlignmentHierarchyTree visions={filteredVisions} orphans={filteredOrphans} />
          ) : null}
        </TabsContent>

        <TabsContent value="handicaps" className="space-y-3">
          {loading && !filteredHandicaps ? (
            <p className="text-sm text-[var(--color-secondary-text)]">{t('alignment.reports.generating')}</p>
          ) : filteredHandicaps ? (
            <div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                  {[
                    filteredHandicaps.summary.total_handicaps,
                    filteredHandicaps.summary.vision_handicaps,
                    filteredHandicaps.summary.objective_handicaps,
                    filteredHandicaps.summary.strategy_kpi_handicaps,
                    filteredHandicaps.summary.strategy_project_handicaps,
                  ].map((value, idx) => {
                    const labels = [
                      t('alignment.reports.totalHandicaps'),
                      t('alignment.reports.visionHandicaps'),
                      t('alignment.reports.objectiveHandicaps'),
                      t('alignment.reports.strategyKPIHandicaps'),
                      t('alignment.reports.strategyProjectHandicaps'),
                    ];
                    return (
                      <div
                        key={idx}
                        className="text-center p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary-bg)]"
                      >
                        <div className="text-lg font-semibold text-[var(--color-surface-text)]">{value}</div>
                        <div className="text-xs text-[var(--color-secondary-text)]">{labels[idx]}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Vision Handicaps */}
                {filteredHandicaps.visions.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold mb-2 text-[var(--color-surface-text)]">{t('alignment.reports.visionsWithoutObjectives')}</h3>
                    <div className="space-y-2">
                      {filteredHandicaps.visions.map((vision) => (
                        <div key={vision.id} className="p-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)]">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-[var(--color-surface-text)]">{vision.name}</h4>
                              <p className="text-sm text-[var(--color-secondary-text)]">{vision.description}</p>
                            </div>
                            <Badge variant="outline">{vision.handicap_type.replace(/_/g, ' ')}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Objective Handicaps */}
                {filteredHandicaps.objectives && filteredHandicaps.objectives.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold mb-2 text-[var(--color-surface-text)]">{t('alignment.reports.objectivesWithoutStrategies')}</h3>
                    <div className="space-y-2">
                      {filteredHandicaps.objectives.map((objective) => (
                        <div key={objective.id} className="p-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)]">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-[var(--color-surface-text)]">{objective.name}</h4>
                              <p className="text-sm text-[var(--color-secondary-text)]">{objective.description}</p>
                            </div>
                            <Badge variant="outline">{objective.handicap_type.replace(/_/g, ' ')}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Strategy KPI Handicaps */}
                {filteredHandicaps.strategies.filter(s => s.handicap_type === 'STRATEGY_WITHOUT_KPIS').length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold mb-2 text-[var(--color-surface-text)]">{t('alignment.reports.strategiesWithoutKPIs')}</h3>
                    <div className="space-y-2">
                      {filteredHandicaps.strategies
                        .filter(s => s.handicap_type === 'STRATEGY_WITHOUT_KPIS')
                        .map((strategy) => (
                        <div key={strategy.id} className="p-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)]">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-[var(--color-surface-text)]">{strategy.name}</h4>
                              <p className="text-sm text-[var(--color-secondary-text)]">{strategy.description}</p>
                            </div>
                            <Badge variant="outline">{strategy.handicap_type.replace(/_/g, ' ')}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Strategy Project Handicaps */}
                {filteredHandicaps.strategiesWithoutProjects.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-2 text-[var(--color-surface-text)]">{t('alignment.reports.strategiesWithoutProjects')}</h3>
                    <div className="space-y-2">
                      {filteredHandicaps.strategiesWithoutProjects.map((strategy) => (
                        <div key={strategy.id} className="p-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)]">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-[var(--color-surface-text)]">{strategy.name}</h4>
                              <p className="text-sm text-[var(--color-secondary-text)]">{strategy.description}</p>
                            </div>
                            <Badge variant="outline">{strategy.handicap_type.replace(/_/g, ' ')}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          ) : null}
        </TabsContent>

        <TabsContent value="orphans" className="space-y-3">
          {loading && !filteredOrphansTab ? (
            <p className="text-sm text-[var(--color-secondary-text)]">{t('alignment.reports.generating')}</p>
          ) : filteredOrphansTab ? (
            <div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  {[
                    filteredOrphansTab.summary.total_orphans,
                    filteredOrphansTab.summary.orphan_objectives || 0,
                    filteredOrphansTab.summary.orphan_strategies,
                    filteredOrphansTab.summary.orphan_projects,
                  ].map((value, idx) => {
                    const labels = [
                      t('alignment.reports.totalOrphans'),
                      t('alignment.reports.orphanObjectives'),
                      t('alignment.reports.orphanStrategies'),
                      t('alignment.reports.orphanProjects'),
                    ];
                    return (
                      <div
                        key={idx}
                        className="text-center p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary-bg)]"
                      >
                        <div className="text-lg font-semibold text-[var(--color-surface-text)]">{value}</div>
                        <div className="text-xs text-[var(--color-secondary-text)]">{labels[idx]}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Orphan Objectives */}
                {filteredOrphansTab.objectives && filteredOrphansTab.objectives.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold mb-2 text-[var(--color-surface-text)]">{t('alignment.reports.objectivesWithoutVision')}</h3>
                    <div className="space-y-2">
                      {filteredOrphansTab.objectives.map((objective) => (
                        <div key={objective.id} className="p-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)]">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-[var(--color-surface-text)]">{objective.name}</h4>
                              <p className="text-sm text-[var(--color-secondary-text)]">{objective.description}</p>
                            </div>
                            <Badge variant="outline">{objective.orphan_type.replace(/_/g, ' ')}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Orphan Strategies */}
                {filteredOrphansTab.strategies.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold mb-2 text-[var(--color-surface-text)]">{t('alignment.reports.strategiesCannotReachVision')}</h3>
                    <div className="space-y-2">
                      {filteredOrphansTab.strategies.map((strategy) => (
                        <div key={strategy.id} className="p-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)]">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-[var(--color-surface-text)]">{strategy.name}</h4>
                              <p className="text-sm text-[var(--color-secondary-text)]">{strategy.description}</p>
                            </div>
                            <Badge variant="outline">{strategy.orphan_type.replace(/_/g, ' ')}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Orphan Projects */}
                {filteredOrphansTab.projects.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold mb-2 text-[var(--color-surface-text)]">{t('alignment.reports.projectsCannotReachVision')}</h3>
                    {(() => {
                      // Group projects by roadmap
                      const projectsByRoadmap = new Map<string, OrphanItem[]>();
                      const ungroupedProjects: OrphanItem[] = [];
                      
                      filteredOrphansTab.projects.forEach(project => {
                        if (project.roadmap_id && project.roadmap_name) {
                          const roadmapKey = `${project.roadmap_id}|${project.roadmap_name}`;
                          if (!projectsByRoadmap.has(roadmapKey)) {
                            projectsByRoadmap.set(roadmapKey, []);
                          }
                          projectsByRoadmap.get(roadmapKey)!.push(project);
                        } else {
                          ungroupedProjects.push(project);
                        }
                      });
                      
                      return (
                        <div className="space-y-4">
                          {/* Grouped by roadmap */}
                          {Array.from(projectsByRoadmap.entries()).map(([roadmapKey, projects]) => {
                            const [, roadmapName] = roadmapKey.split('|');
                            return (
                              <div key={roadmapKey} className="border border-[var(--color-border)] rounded-lg overflow-hidden">
                                <div className="bg-[var(--color-accent-bg)] px-3 py-2 border-b border-[var(--color-border)]">
                                  <h4 className="font-semibold text-[var(--color-surface-text)]">{roadmapName}</h4>
                                  <p className="text-xs text-[var(--color-secondary-text)]">{projects.length} {projects.length !== 1 ? t('alignment.reports.projects') : t('alignment.reports.project')}</p>
                                </div>
                                <div className="space-y-1 p-2">
                                  {projects.map((project) => (
                                    <div key={project.id} className="px-2 py-1.5 rounded text-xs border border-[var(--color-border)] bg-[var(--color-surface-bg)]">
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                          <span className="font-medium text-[var(--color-surface-text)]">{project.name}</span>
                                          {project.description && (
                                            <span className="text-[var(--color-secondary-text)] ml-2">({project.description})</span>
                                          )}
                                        </div>
                                        <Badge variant="outline">{project.orphan_type.replace(/_/g, ' ')}</Badge>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                          
                          {/* Ungrouped projects (no roadmap) */}
                          {ungroupedProjects.length > 0 && (
                            <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
                              <div className="bg-[var(--color-accent-bg)] px-3 py-2 border-b border-[var(--color-border)]">
                                <h4 className="font-semibold text-[var(--color-surface-text)]">{t('alignment.reports.noRoadmap')}</h4>
                                <p className="text-xs text-[var(--color-secondary-text)]">{ungroupedProjects.length} {ungroupedProjects.length !== 1 ? t('alignment.reports.projects') : t('alignment.reports.project')}</p>
                              </div>
                              <div className="space-y-1 p-2">
                                {ungroupedProjects.map((project) => (
                                  <div key={project.id} className="px-2 py-1.5 rounded text-xs border border-[var(--color-border)] bg-[var(--color-surface-bg)]">
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1 min-w-0">
                                        <span className="font-medium text-[var(--color-surface-text)]">{project.name}</span>
                                        {project.description && (
                                          <span className="text-[var(--color-secondary-text)] ml-2">({project.description})</span>
                                        )}
                                      </div>
                                      <Badge variant="outline">{project.orphan_type.replace(/_/g, ' ')}</Badge>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

            </div>
          ) : null}
        </TabsContent>

      </Tabs>
    </div>
  );
};
